// src/Pages/Login.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { loginInitiate } from "../redux/actions/loginAction";
import sun from "../assets/sun.png";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, error } = useSelector((state) => state.login || {});

  const [userID, setUserID] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Helper: redirect based on role
  const redirectByRole = (user) => {
    if (user?.role === "ADMIN" || user?.role === "STAFF") {
      navigate("/admin/dashboard", { replace: true });
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  // Redirect if already logged in (on mount)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");

    if (token && user) {
      redirectByRole(user);
      return;
    }

    // Listen for token set in another tab
    const onStorage = (e) => {
      if (e.key === "token" && e.newValue) {
        const u = JSON.parse(localStorage.getItem("user") || "null");
        redirectByRole(u);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);

  // Poll for token (handles async login flows)
  useEffect(() => {
    const t = setInterval(() => {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (token && user) {
        clearInterval(t);
        redirectByRole(user);
      }
    }, 300);
    return () => clearInterval(t);
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userID.trim() || !password.trim()) {
      return;
    }
    dispatch(loginInitiate({ userID, password }, navigate));
  };

  return (
    <>
      <style>{`
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .login-page {
          min-height: calc(100vh - 72px);
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 40px 20px;
          background: linear-gradient(135deg, #004d00 0%, #006600 100%);
        }

        .login-card {
          width: 100%;
          max-width: 380px;
          padding: 34px 26px;
          text-align: center;
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.25);
          margin-top: 20px;
        }

        .login-title {
          font-size: 28px;
          font-weight: 800;
          color: #111;
          margin-bottom: 4px;
        }

        .login-subtitle {
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 1px;
          color: #006600;
          margin-bottom: 20px;
        }

        .login-logo-wrapper {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: linear-gradient(135deg, #004d00 0%, #006600 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          box-shadow: 0 4px 15px rgba(0, 102, 0, 0.4);
          border: 2px solid rgba(255, 255, 255, 0.4);
        }

        .login-logo {
          width: 70px;
          height: 70px;
          object-fit: contain;
          border-radius: 50%;
        }

        .login-input {
          width: 100%;
          height: 46px;
          border: 1.5px solid #dcdfe6;
          border-radius: 10px;
          padding: 0 14px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .login-input:focus {
          border-color: #006600;
          box-shadow: 0 0 0 3px rgba(0, 102, 0, 0.15);
        }

        .input-group {
          position: relative;
          margin-bottom: 16px;
          width: 100%;
        }

        .eye-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #909399;
          font-size: 18px;
          display: flex;
          align-items: center;
          padding: 4px;
          transition: color 0.2s;
        }

        .eye-toggle:hover {
          color: #006600;
        }

        .innovative-text {
          margin-top: 24px;
          padding-top: 18px;
          border-top: 1px dashed #eee;
          font-size: 11px;
          line-height: 1.5;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .login-input.input-error {
          border-color: #b00020;
          box-shadow: 0 0 0 3px rgba(176, 0, 32, 0.1);
        }

        .login-btn {
          width: 100%;
          height: 48px;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #004d00 0%, #006600 100%);
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          margin-top: 6px;
        }

        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(0, 102, 0, 0.3);
        }

        .login-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-bottomLinks {
          margin-top: 14px;
          font-size: 14px;
          color: #555;
        }

        .login-bottomLinks a {
          color: #006600;
          font-weight: 700;
          text-decoration: none;
          transition: color 0.15s;
        }

        .login-bottomLinks a:hover {
          color: #004d00;
          text-decoration: underline;
        }

        .login-error-box {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-top: 16px;
          padding: 12px 14px;
          border-radius: 8px;
          background: rgba(176, 0, 32, 0.05);
          border: 1px solid rgba(176, 0, 32, 0.2);
          color: #b00020;
          font-size: 13px;
          font-weight: 600;
          text-align: left;
          animation: shake 0.35s ease;
        }

        .login-error-icon {
          flex-shrink: 0;
          margin-top: 1px;
          font-size: 15px;
        }

        @keyframes shake {
          0%   { transform: translateX(0); }
          20%  { transform: translateX(-5px); }
          40%  { transform: translateX(5px); }
          60%  { transform: translateX(-4px); }
          80%  { transform: translateX(4px); }
          100% { transform: translateX(0); }
        }
      `}</style>

      <main className="login-page">
        <div className="login-card">
          <div className="login-title">Login</div>
          <div className="login-subtitle">FOR A BRIGHTER FUTURE</div>

          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: '1.6', fontWeight: '500' }}>
            Welcome back! Sign in to manage your assets, <br />
            track your earnings, and grow with us.
          </div>

          <div className="login-logo-wrapper">
            <img src={sun} alt="Sun Logo" className="login-logo" />
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="input-group">
              <input
                className={`login-input${error ? " input-error" : ""}`}
                type="text"
                placeholder="Login ID"
                value={userID}
                onChange={(e) => setUserID(e.target.value.toUpperCase())}
                autoComplete="username"
              />
            </div>

            <div className="input-group">
              <input
                className={`login-input${error ? " input-error" : ""}`}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                className="eye-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? "👁️" : "🙈"}
              </button>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? "Signing In…" : "Login"}
            </button>

            <div className="innovative-text" style={{ marginTop: '20px', borderTop: 'none', paddingTop: '0' }}>
              Your gateway to financial independence <br /> and collaborative growth.
            </div>

            {error && (
              <div className="login-error-box" key={error}>
                <span className="login-error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div className="login-bottomLinks">
              New user? <Link to="/register">Register</Link>
            </div>
          </form>
        </div>
      </main>
    </>
  );
}